import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const ReconciliationForm = ({ activeDrawer, varianceThreshold, onComplete }) => {
  const { user, currentTenant } = useAuth();
  const [loading, setLoading] = useState(false);
  const [expectedBalance, setExpectedBalance] = useState(0);
  const [varianceReason, setVarianceReason] = useState('');
  const [notes, setNotes] = useState('');
  const [denominations, setDenominations] = useState([
    { type: 'bill', value: 100, quantity: 0 },
    { type: 'bill', value: 50, quantity: 0 },
    { type: 'bill', value: 20, quantity: 0 },
    { type: 'bill', value: 10, quantity: 0 },
    { type: 'bill', value: 5, quantity: 0 },
    { type: 'bill', value: 1, quantity: 0 },
    { type: 'coin', value: 0.25, quantity: 0 },
    { type: 'coin', value: 0.10, quantity: 0 },
    { type: 'coin', value: 0.05, quantity: 0 },
    { type: 'coin', value: 0.01, quantity: 0 }
  ]);

  useEffect(() => {
    if (activeDrawer) {
      setExpectedBalance(activeDrawer?.current_balance || 0);
    }
  }, [activeDrawer]);

  const actualBalance = denominations?.reduce(
    (sum, denom) => sum + (denom?.value * denom?.quantity),
    0
  );

  const variance = actualBalance - expectedBalance;

  const updateDenomination = (index, quantity) => {
    const updated = [...denominations];
    updated[index].quantity = parseInt(quantity) || 0;
    setDenominations(updated);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (Math.abs(variance) > varianceThreshold && !varianceReason?.trim()) {
      alert('Please provide a reason for the variance');
      return;
    }

    setLoading(true);
    try {
      const { data: reconciliation, error: reconciliationError } = await supabase?.from('cash_reconciliations')?.insert({
          tenant_id: currentTenant?.id,
          drawer_id: activeDrawer?.id,
          expected_balance: expectedBalance,
          actual_balance: actualBalance,
          variance_reason: varianceReason?.trim() || null,
          reconciled_by: user?.id,
          notes: notes?.trim() || null,
          status: Math.abs(variance) <= varianceThreshold ? 'approved' : 'pending',
          alert_sent: false
        })?.select()?.single();

      if (reconciliationError) throw reconciliationError;

      // Insert denomination breakdown
      const denominationRecords = denominations
        ?.filter(d => d?.quantity > 0)
        ?.map(d => ({
          reconciliation_id: reconciliation?.id,
          denomination_type: d?.type,
          denomination_value: d?.value,
          quantity: d?.quantity
        }));

      if (denominationRecords?.length > 0) {
        const { error: denomError } = await supabase?.from('cash_denominations')?.insert(denominationRecords);

        if (denomError) throw denomError;
      }

      // Reset form
      setDenominations(denominations?.map(d => ({ ...d, quantity: 0 })));
      setVarianceReason('');
      setNotes('');
      
      alert('Reconciliation completed successfully!');
      onComplete?.();
    } catch (error) {
      console.error('Error submitting reconciliation:', error);
      alert('Failed to complete reconciliation');
    } finally {
      setLoading(false);
    }
  };

  if (!activeDrawer) {
    return (
      <div className="bg-card rounded-lg shadow-md p-6 border border-border">
        <div className="text-center text-muted-foreground py-8">
          <Icon name="Lock" size={48} className="mx-auto mb-4 opacity-50" />
          <p className="text-lg font-medium">No Active Drawer</p>
          <p className="text-sm mt-2">Open a cash drawer to perform reconciliation</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg shadow-md p-6 border border-border">
      <div className="flex items-center gap-2 mb-6">
        <Icon name="Calculator" size={24} className="text-primary" />
        <h2 className="text-xl font-bold text-foreground">Reconciliation Form</h2>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Balance Summary */}
        <div className="grid grid-cols-3 gap-4 p-4 bg-background rounded-lg border border-border">
          <div>
            <p className="text-sm text-muted-foreground mb-1">Expected</p>
            <p className="text-lg font-bold text-foreground">${expectedBalance?.toFixed(2)}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground mb-1">Actual</p>
            <p className="text-lg font-bold text-primary">${actualBalance?.toFixed(2)}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground mb-1">Variance</p>
            <p className={`text-lg font-bold ${
              variance === 0 ? 'text-green-500' : 
              Math.abs(variance) <= varianceThreshold ? 'text-yellow-500' : 'text-red-500'
            }`}>
              {variance >= 0 ? '+' : ''}${variance?.toFixed(2)}
            </p>
          </div>
        </div>

        {/* Variance Alert */}
        {Math.abs(variance) > varianceThreshold && (
          <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
            <div className="flex items-start gap-2">
              <Icon name="AlertTriangle" size={20} className="text-red-500 mt-0.5" />
              <div>
                <p className="font-semibold text-red-500 mb-1">Variance Alert</p>
                <p className="text-sm text-foreground">
                  Variance exceeds threshold of ${varianceThreshold?.toFixed(2)}. 
                  An email alert will be sent to management.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Bills */}
        <div>
          <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
            <Icon name="Banknote" size={18} />
            Bills
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {denominations?.filter(d => d?.type === 'bill')?.map((denom, index) => (
              <div key={index} className="flex items-center gap-2">
                <label className="text-sm font-medium text-foreground w-16">
                  ${denom?.value}
                </label>
                <Input
                  type="number"
                  min="0"
                  value={denom?.quantity}
                  onChange={(e) => updateDenomination(index, e?.target?.value)}
                  className="flex-1"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Coins */}
        <div>
          <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
            <Icon name="Coins" size={18} />
            Coins
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {denominations?.filter(d => d?.type === 'coin')?.map((denom, index) => (
              <div key={index} className="flex items-center gap-2">
                <label className="text-sm font-medium text-foreground w-12">
                  ${denom?.value?.toFixed(2)}
                </label>
                <Input
                  type="number"
                  min="0"
                  value={denom?.quantity}
                  onChange={(e) => updateDenomination(denominations?.findIndex(d => d === denom), e?.target?.value)}
                  className="flex-1"
                />
              </div>
            ))}
          </div>
        </div>

        {/* Variance Reason */}
        {Math.abs(variance) > 0 && (
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Variance Reason {Math.abs(variance) > varianceThreshold && <span className="text-red-500">*</span>}
            </label>
            <Input
              value={varianceReason}
              onChange={(e) => setVarianceReason(e?.target?.value)}
              placeholder="Explain the reason for variance"
            />
          </div>
        )}

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Additional Notes
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e?.target?.value)}
            placeholder="Any additional observations or comments"
            rows={3}
            className="w-full px-3 py-2 rounded-lg bg-background border border-border focus:outline-none focus:ring-2 focus:ring-primary text-foreground"
          />
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          disabled={loading || actualBalance === 0}
          className="w-full"
        >
          {loading ? 'Submitting...' : 'Complete Reconciliation'}
        </Button>
      </form>
    </div>
  );
};

export default ReconciliationForm;